import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Nav, Button, Modal, Badge, Form } from 'react-bootstrap';
import { API_BASE_URL } from '../config/api';
import SuperAdminSidebar from '../components/SuperAdminSidebar';
import AdminTopBar from '../components/AdminTopBar';
import { useInstitutionalData, useInstitutionalResources } from '../contexts/InstitutionalDataContext';

const resolveLogoSrc = (logoUrl) => {
  if (!logoUrl) return '';
  if (/^https?:\/\//i.test(logoUrl)) return logoUrl;
  return `${API_BASE_URL}${logoUrl}`;
};

const InstitutionalSettingsPage = () => {
  const [institutionOverride, setInstitutionOverride] = useState(null);
  const { invalidate } = useInstitutionalData();
  const { entries, loading: resourcesLoading, error: resourceError } = useInstitutionalResources([
    'institution',
  ]);
  const institution = institutionOverride || entries.institution?.data || null;
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const loading = resourcesLoading && !institution;
  const [activeTab, setActiveTab] = useState('overview');

  const [showEditInstitution, setShowEditInstitution] = useState(false);
  const [editInstitutionData, setEditInstitutionData] = useState({
    name: '',
    type: '',
    address: '',
    website: '',
    primaryContactEmail: '',
    phoneNumber: '',
    logoUrl: ''
  });

  // Branding-specific modal
  const [showEditBranding, setShowEditBranding] = useState(false);
  const [brandingData, setBrandingData] = useState({
    name: '',
    website: '',
    logoUrl: '',
  });
  const [brandingFile, setBrandingFile] = useState(null);
  const [brandingFilePreview, setBrandingFilePreview] = useState('');
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandingError, setBrandingError] = useState('');
  const brandingFileInputRef = useRef(null);

  // Preferences (client-side only placeholders; ready to be wired to API later)
  const [preferences, setPreferences] = useState({
    timezone: 'America/New_York',
    weekStart: 'sunday',
    dateFormat: 'MM/DD/YYYY',
    defaultLanding: 'dashboard',
  });

  // Notification preferences (client-side only placeholders)
  const [notifications, setNotifications] = useState({
    emailAnnouncements: true,
    emailEvaluations: true,
    emailScheduleChanges: true,
    digestFrequency: 'daily',
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token || role !== 'institutional_admin') {
      window.location.replace('/');
    }
  }, []);

  useEffect(() => {
    if (resourceError) setError(resourceError);
  }, [resourceError]);

  const refreshInstitutionalData = () => {
    invalidate(['institution']);
  };

  const breadcrumbItems = [
    { href: '/institutional-admin/dashboard', label: 'Dashboard' },
    { label: 'Settings' }
  ];

  const handleEditInstitution = () => {
    if (institution) {
      setEditInstitutionData({
        name: institution.name || '',
        type: institution.type || '',
        address: institution.address || '',
        website: institution.website || '',
        primaryContactEmail: institution.primaryContactEmail || '',
        phoneNumber: institution.phoneNumber || '',
        logoUrl: institution.logoUrl || ''
      });
      setShowEditInstitution(true);
    }
  };

  const handleUpdateInstitution = async (e) => {
    e.preventDefault();
    try {
      if (editInstitutionData.website && editInstitutionData.website.trim() !== '') {
        try {
          new URL(editInstitutionData.website);
        } catch (urlError) {
          setError('Please enter a valid website URL (e.g., https://example.com)');
          return;
        }
      }

      if (
        editInstitutionData.logoUrl &&
        editInstitutionData.logoUrl.trim() !== '' &&
        !editInstitutionData.logoUrl.startsWith('/uploads/')
      ) {
        try {
          new URL(editInstitutionData.logoUrl);
        } catch (urlError) {
          setError('Please enter a valid logo URL (e.g., https://example.com/logo.png)');
          return;
        }
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/institutional-admin/institution`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editInstitutionData)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to update institution';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setInstitutionOverride(data.institution);
      refreshInstitutionalData();
      setShowEditInstitution(false);
      setEditInstitutionData({
        name: '',
        type: '',
        address: '',
        website: '',
        primaryContactEmail: '',
        phoneNumber: '',
        logoUrl: ''
      });
      setSuccessMsg('Institution details updated successfully.');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error('Error updating institution:', err);
      setError(err.message);
    }
  };

  const openBrandingModal = () => {
    setBrandingData({
      name: institution?.name || '',
      website: institution?.website || '',
      logoUrl: institution?.logoUrl || '',
    });
    setBrandingFile(null);
    setBrandingFilePreview('');
    setBrandingError('');
    setShowEditBranding(true);
  };

  const closeBrandingModal = () => {
    setShowEditBranding(false);
    setBrandingFile(null);
    setBrandingFilePreview('');
    setBrandingError('');
    if (brandingFileInputRef.current) {
      brandingFileInputRef.current.value = '';
    }
  };

  const handleBrandingFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setBrandingError('');
    if (!file) {
      setBrandingFile(null);
      setBrandingFilePreview('');
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      setBrandingError('Please select a JPEG, PNG, GIF, WebP, or SVG image.');
      setBrandingFile(null);
      setBrandingFilePreview('');
      if (brandingFileInputRef.current) brandingFileInputRef.current.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setBrandingError('File is too large (max 5 MB).');
      setBrandingFile(null);
      setBrandingFilePreview('');
      if (brandingFileInputRef.current) brandingFileInputRef.current.value = '';
      return;
    }
    setBrandingFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setBrandingFilePreview(ev.target?.result || '');
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = async () => {
    if (!institution?.logoUrl) {
      setBrandingData({ ...brandingData, logoUrl: '' });
      setBrandingFile(null);
      setBrandingFilePreview('');
      return;
    }
    if (!window.confirm('Remove the current logo?')) return;
    try {
      setBrandingSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/institutional-admin/institution/logo`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to remove logo');
      }
      const data = await response.json();
      setInstitutionOverride(data.institution);
      refreshInstitutionalData();
      setBrandingData((prev) => ({ ...prev, logoUrl: '' }));
      setBrandingFile(null);
      setBrandingFilePreview('');
      if (brandingFileInputRef.current) brandingFileInputRef.current.value = '';
      setSuccessMsg('Logo removed.');
      setTimeout(() => setSuccessMsg(''), 2500);
    } catch (err) {
      setBrandingError(err.message);
    } finally {
      setBrandingSaving(false);
    }
  };

  const handleSaveBranding = async (e) => {
    e.preventDefault();
    setBrandingError('');

    if (brandingData.website && brandingData.website.trim() !== '') {
      try {
        new URL(brandingData.website);
      } catch (urlError) {
        setBrandingError('Please enter a valid website URL (e.g., https://example.com).');
        return;
      }
    }

    // Only validate logoUrl as a URL when no file is being uploaded and the
    // value is not an internal /uploads/... path.
    if (!brandingFile && brandingData.logoUrl && brandingData.logoUrl.trim() !== '' && !brandingData.logoUrl.startsWith('/uploads/')) {
      try {
        new URL(brandingData.logoUrl);
      } catch (urlError) {
        setBrandingError('Please enter a valid logo URL or upload a file.');
        return;
      }
    }

    try {
      setBrandingSaving(true);
      const token = localStorage.getItem('token');
      let latestInstitution = null;

      if (brandingFile) {
        const formData = new FormData();
        formData.append('logo', brandingFile);
        const uploadResp = await fetch(`${API_BASE_URL}/api/institutional-admin/institution/logo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!uploadResp.ok) {
          const data = await uploadResp.json().catch(() => ({}));
          throw new Error(data.message || 'Failed to upload logo');
        }
        const uploadData = await uploadResp.json();
        latestInstitution = uploadData.institution;
      }

      // Decide what logoUrl to save on the institution. If the user uploaded
      // a file we already saved it server-side; otherwise use the typed value.
      const effectiveLogoUrl = brandingFile
        ? (latestInstitution?.logoUrl ?? null)
        : (brandingData.logoUrl || null);

      const putResp = await fetch(`${API_BASE_URL}/api/institutional-admin/institution`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: brandingData.name,
          website: brandingData.website,
          logoUrl: effectiveLogoUrl === null ? '' : effectiveLogoUrl,
        }),
      });
      if (!putResp.ok) {
        const data = await putResp.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to save branding');
      }
      const putData = await putResp.json();
      setInstitutionOverride(putData.institution);
      refreshInstitutionalData();
      setSuccessMsg('Branding updated successfully.');
      setTimeout(() => setSuccessMsg(''), 3000);
      closeBrandingModal();
    } catch (err) {
      setBrandingError(err.message);
    } finally {
      setBrandingSaving(false);
    }
  };

  const savePreferencesPlaceholder = (e) => {
    e.preventDefault();
    setSuccessMsg('Preferences saved.');
    setTimeout(() => setSuccessMsg(''), 2500);
  };

  const saveNotificationsPlaceholder = (e) => {
    e.preventDefault();
    setSuccessMsg('Notification settings saved.');
    setTimeout(() => setSuccessMsg(''), 2500);
  };

  if (loading) {
    return (
      <div className="d-flex vh-100 overflow-hidden bg-light">
        <SuperAdminSidebar variant="institutional" />
        <div className="d-flex flex-column flex-grow-1 min-w-0 overflow-auto justify-content-center align-items-center">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex vh-100">
      <SuperAdminSidebar variant="institutional" />
      <div className="d-flex flex-column flex-grow-1 min-w-0 overflow-auto">
        <AdminTopBar variant="institutional" breadcrumbItems={breadcrumbItems} />
        <div className="flex-grow-1">
          <Container fluid className="py-3">
            <Row className="mb-3">
              <Col md={12}>
                <div className="d-flex align-items-center gap-3 mb-2">
                  {institution?.logoUrl && (
                    <img
                      src={resolveLogoSrc(institution.logoUrl)}
                      alt=""
                      style={{ height: 44, width: 44, objectFit: 'contain', borderRadius: 6 }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div>
                    <h4 className="mb-0">Settings</h4>
                    <div className="text-muted small">{institution?.name || 'Institution'}</div>
                  </div>
                </div>
                <Nav
                  variant="pills"
                  className="gap-2 flex-wrap"
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k || 'overview')}
                >
                  <Nav.Item>
                    <Nav.Link eventKey="overview">Overview</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="preferences">Preferences</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="branding">Branding</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="notifications">Notifications</Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
            </Row>

            {error && <Alert variant="danger" className="mb-3" onClose={() => setError('')} dismissible>{error}</Alert>}
            {successMsg && <Alert variant="success" className="mb-3" onClose={() => setSuccessMsg('')} dismissible>{successMsg}</Alert>}

            {activeTab === 'overview' && (
              <Row>
                <Col md={12}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Institution Overview</h5>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleEditInstitution}
                        title="Edit Institution Details"
                      >
                        <i className="bi bi-pencil"></i> Edit
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      {institution && (
                        <Row>
                          <Col md={6}>
                            <p><strong>Name:</strong> {institution.name}</p>
                            <p><strong>Type:</strong> {institution.type || 'Not specified'}</p>
                            <p><strong>Address:</strong> {institution.address || 'Not specified'}</p>
                            <p><strong>Website:</strong> {institution.website ? (
                              <a href={institution.website} target="_blank" rel="noopener noreferrer">
                                {institution.website}
                              </a>
                            ) : 'Not specified'}</p>
                          </Col>
                          <Col md={6}>
                            <p><strong>Primary Contact:</strong> {institution.primaryContactEmail || 'Not specified'}</p>
                            <p><strong>Phone:</strong> {institution.phoneNumber || 'Not specified'}</p>
                            <p><strong>Status:</strong>{' '}
                              <Badge bg={institution.isActive ? 'success' : 'danger'}>
                                {institution.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </p>
                          </Col>
                        </Row>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {activeTab === 'preferences' && (
              <Row>
                <Col md={12}>
                  <Card>
                    <Card.Header>
                      <h5 className="mb-0">Preferences</h5>
                      <div className="text-muted small">Institution-wide defaults for scheduling and display</div>
                    </Card.Header>
                    <Card.Body>
                      <Form onSubmit={savePreferencesPlaceholder}>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Timezone</Form.Label>
                              <Form.Select
                                value={preferences.timezone}
                                onChange={(e) => setPreferences({ ...preferences, timezone: e.target.value })}
                              >
                                <option value="America/New_York">America/New_York</option>
                                <option value="America/Chicago">America/Chicago</option>
                                <option value="America/Denver">America/Denver</option>
                                <option value="America/Los_Angeles">America/Los_Angeles</option>
                                <option value="UTC">UTC</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Week Starts On</Form.Label>
                              <Form.Select
                                value={preferences.weekStart}
                                onChange={(e) => setPreferences({ ...preferences, weekStart: e.target.value })}
                              >
                                <option value="sunday">Sunday</option>
                                <option value="monday">Monday</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Date Format</Form.Label>
                              <Form.Select
                                value={preferences.dateFormat}
                                onChange={(e) => setPreferences({ ...preferences, dateFormat: e.target.value })}
                              >
                                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Default Landing Page</Form.Label>
                              <Form.Select
                                value={preferences.defaultLanding}
                                onChange={(e) => setPreferences({ ...preferences, defaultLanding: e.target.value })}
                              >
                                <option value="dashboard">Dashboard</option>
                                <option value="calendar">Calendar</option>
                                <option value="user-program-management">User/Program Management</option>
                                <option value="evaluations">Evaluations</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="d-flex justify-content-end">
                          <Button type="submit" variant="primary">Save Preferences</Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {activeTab === 'branding' && (
              <Row>
                <Col md={12}>
                  <Card>
                    <Card.Header>
                      <h5 className="mb-0">Branding</h5>
                      <div className="text-muted small">How your institution appears to users</div>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={4} className="text-center">
                          {institution?.logoUrl ? (
                            <img
                              src={resolveLogoSrc(institution.logoUrl)}
                              alt="Institution logo"
                              style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-center border rounded text-muted"
                              style={{ height: 120 }}
                            >
                              No logo uploaded
                            </div>
                          )}
                          <div className="mt-2 small text-muted">Institution Logo</div>
                        </Col>
                        <Col md={8}>
                          <p className="mb-1"><strong>Display Name:</strong> {institution?.name || '—'}</p>
                          <p className="mb-1"><strong>Logo:</strong>{' '}
                            {institution?.logoUrl ? (
                              <a href={resolveLogoSrc(institution.logoUrl)} target="_blank" rel="noopener noreferrer">
                                {institution.logoUrl}
                              </a>
                            ) : 'Not specified'}
                          </p>
                          <p className="mb-3"><strong>Website:</strong>{' '}
                            {institution?.website ? (
                              <a href={institution.website} target="_blank" rel="noopener noreferrer">
                                {institution.website}
                              </a>
                            ) : 'Not specified'}
                          </p>
                          <Button variant="outline-primary" size="sm" onClick={openBrandingModal}>
                            <i className="bi bi-pencil"></i> Update Branding
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {activeTab === 'notifications' && (
              <Row>
                <Col md={12}>
                  <Card>
                    <Card.Header>
                      <h5 className="mb-0">Notifications</h5>
                      <div className="text-muted small">Default email notification settings for your institution</div>
                    </Card.Header>
                    <Card.Body>
                      <Form onSubmit={saveNotificationsPlaceholder}>
                        <Form.Check
                          type="switch"
                          id="notif-announcements"
                          label="Email announcements to all users"
                          checked={notifications.emailAnnouncements}
                          onChange={(e) => setNotifications({ ...notifications, emailAnnouncements: e.target.checked })}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="notif-evaluations"
                          label="Email evaluation reminders"
                          checked={notifications.emailEvaluations}
                          onChange={(e) => setNotifications({ ...notifications, emailEvaluations: e.target.checked })}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="notif-schedule"
                          label="Email schedule change notifications"
                          checked={notifications.emailScheduleChanges}
                          onChange={(e) => setNotifications({ ...notifications, emailScheduleChanges: e.target.checked })}
                          className="mb-3"
                        />
                        <Form.Group className="mb-3" style={{ maxWidth: 320 }}>
                          <Form.Label>Digest Frequency</Form.Label>
                          <Form.Select
                            value={notifications.digestFrequency}
                            onChange={(e) => setNotifications({ ...notifications, digestFrequency: e.target.value })}
                          >
                            <option value="realtime">Realtime</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="off">Off</option>
                          </Form.Select>
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                          <Button type="submit" variant="primary">Save Notifications</Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Container>
        </div>
      </div>

      <Modal show={showEditInstitution} onHide={() => setShowEditInstitution(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Institution Details</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateInstitution}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Institution Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={editInstitutionData.name}
                    onChange={(e) => setEditInstitutionData({ ...editInstitutionData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    value={editInstitutionData.type}
                    onChange={(e) => setEditInstitutionData({ ...editInstitutionData, type: e.target.value })}
                  >
                    <option value="">Select Type</option>
                    <option value="university">University</option>
                    <option value="hospital">Hospital</option>
                    <option value="health_system">Health System</option>
                    <option value="clinic">Clinic</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={editInstitutionData.address}
                    onChange={(e) => setEditInstitutionData({ ...editInstitutionData, address: e.target.value })}
                    placeholder="Enter institution address"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Website</Form.Label>
                  <Form.Control
                    type="url"
                    value={editInstitutionData.website}
                    onChange={(e) => setEditInstitutionData({ ...editInstitutionData, website: e.target.value })}
                    placeholder="https://example.com"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    value={editInstitutionData.phoneNumber}
                    onChange={(e) => setEditInstitutionData({ ...editInstitutionData, phoneNumber: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Primary Contact Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={editInstitutionData.primaryContactEmail}
                    onChange={(e) => setEditInstitutionData({ ...editInstitutionData, primaryContactEmail: e.target.value })}
                    placeholder="contact@institution.com"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Logo URL</Form.Label>
                  <Form.Control
                    type="url"
                    value={editInstitutionData.logoUrl}
                    onChange={(e) => setEditInstitutionData({ ...editInstitutionData, logoUrl: e.target.value })}
                    placeholder="https://example.com/logo.png"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditInstitution(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Institution
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Branding-specific edit modal */}
      <Modal show={showEditBranding} onHide={closeBrandingModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Update Branding</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveBranding}>
          <Modal.Body>
            {brandingError && (
              <Alert variant="danger" onClose={() => setBrandingError('')} dismissible>
                {brandingError}
              </Alert>
            )}

            <Row>
              <Col md={4} className="text-center">
                <div
                  className="border rounded d-flex align-items-center justify-content-center mb-2"
                  style={{ height: 140, background: 'var(--bs-light, #f8f9fa)' }}
                >
                  {brandingFilePreview ? (
                    <img
                      src={brandingFilePreview}
                      alt="Logo preview"
                      style={{ maxHeight: 130, maxWidth: '100%', objectFit: 'contain' }}
                    />
                  ) : brandingData.logoUrl ? (
                    <img
                      src={resolveLogoSrc(brandingData.logoUrl)}
                      alt="Current logo"
                      style={{ maxHeight: 130, maxWidth: '100%', objectFit: 'contain' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-muted small">No logo</span>
                  )}
                </div>
                <div className="small text-muted">
                  {brandingFile ? `Selected: ${brandingFile.name}` : 'Current logo'}
                </div>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={brandingData.name}
                    onChange={(e) => setBrandingData({ ...brandingData, name: e.target.value })}
                    required
                    placeholder="Your institution's display name"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Website</Form.Label>
                  <Form.Control
                    type="url"
                    value={brandingData.website}
                    onChange={(e) => setBrandingData({ ...brandingData, website: e.target.value })}
                    placeholder="https://example.com"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Logo URL</Form.Label>
                  <Form.Control
                    type="text"
                    value={brandingData.logoUrl}
                    onChange={(e) => setBrandingData({ ...brandingData, logoUrl: e.target.value })}
                    placeholder="https://example.com/logo.png"
                    disabled={!!brandingFile}
                  />
                  <Form.Text className="text-muted">
                    {brandingFile
                      ? 'Using the uploaded file. Clear the selection to edit the URL.'
                      : 'Paste an image URL, or upload a file below.'}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>Upload Logo File</Form.Label>
                  <Form.Control
                    ref={brandingFileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                    onChange={handleBrandingFileChange}
                  />
                  <Form.Text className="text-muted">
                    PNG, JPEG, GIF, WebP, or SVG. Max 5 MB.
                  </Form.Text>
                </Form.Group>

                <div className="d-flex gap-2 flex-wrap">
                  {brandingFile && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      type="button"
                      onClick={() => {
                        setBrandingFile(null);
                        setBrandingFilePreview('');
                        if (brandingFileInputRef.current) brandingFileInputRef.current.value = '';
                      }}
                    >
                      Clear selection
                    </Button>
                  )}
                  {institution?.logoUrl && !brandingFile && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      type="button"
                      onClick={handleRemoveLogo}
                      disabled={brandingSaving}
                    >
                      Remove current logo
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeBrandingModal} disabled={brandingSaving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={brandingSaving}>
              {brandingSaving ? (
                <>
                  <Spinner as="span" size="sm" animation="border" className="me-2" />
                  Saving…
                </>
              ) : (
                'Save Branding'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default InstitutionalSettingsPage;
